/**
 * Orden de pedido de personal extra (camareros de empresa externa).
 *
 * Vive en `presupuesto.orden_personal_extra`, también en multievento: UNA orden
 * para todo el evento, con un bloque por jornada (cada grupo lleva su
 * `jornada_id` y su `fecha`) y un solo envío o PDF al proveedor. Las órdenes
 * antiguas por jornada (`jornadas[].orden_personal_extra`) se juntan en una
 * sola la primera vez que se abre (queda como borrador para guardar) y, una vez
 * guardada la de la raíz, se ignoran (no se borran).
 *
 * Regla de negocio (replicada del backend, `backend/utils.py`): si la orden
 * tiene personas, su coste real (Σ personas × horas × tarifa) SUSTITUYE al coste
 * teórico de camareros de los menús de esa jornada. El personal propio y los
 * extras de los menús no se tocan.
 */

import React, { useEffect, useState } from 'react';
import { Users, Plus, Minus, Trash2, Save, AlertTriangle, Download, Send, Pencil, MessageCircle } from 'lucide-react';
import { presupuestosAPI } from '../api/api';
import { Badge } from './ui/badge';
import { textoServicioGrupo } from '../utils/quienVaEvento';
import {
  TARIFA_POR_DEFECTO, calcularCosteOrdenPersonal, esMultievento, fecha10, horasDeGrupo, jornadaDeGrupo,
  ordenDeJornada, tramosDeGrupo,
} from '../utils/ordenPersonalExtra';

const PROVEEDOR_POR_DEFECTO = 'Carlos Rollán';
const EMAIL_POR_DEFECTO = 'manu.rdo@gmail.com';
const TELEFONO_POR_DEFECTO = '619 84 88 28';  // WhatsApp de Carlos Rollán
const HORAS_POR_DEFECTO = 4;
// Marca del guardado de la orden en el PUT del presupuesto (ver presupuesto_merge.py)
const ORIGEN_GUARDADO = 'orden_personal_extra';

// Cálculo de la orden (réplica de `calcular_coste_orden_personal` del backend)
// y reparto por jornada: en utils/ordenPersonalExtra.js (también lo usa la
// tarjeta de coste interno de personal). Se reexportan para quien los importe
// desde aquí.
export { calcularCosteOrdenPersonal, esMultievento, jornadaDeGrupo, ordenDeJornada, tramosDeGrupo };

// ---------------------------------------------------------------------------
// Multievento: órdenes antiguas por jornada
// ---------------------------------------------------------------------------

/** Las órdenes antiguas por jornada juntas en una (cada grupo con su jornada). */
export const fusionarOrdenesJornadas = (presupuesto) => {
  const partes = (presupuesto?.jornadas || [])
    .filter(j => j && j.orden_personal_extra && typeof j.orden_personal_extra === 'object')
    .map(j => [j, j.orden_personal_extra]);
  if (partes.length === 0) return null;
  const base = partes[0][1];
  const tarifaDe = (o) => {
    const t = parseFloat(o?.tarifa_hora);
    return o?.tarifa_hora === null || o?.tarifa_hora === undefined || o?.tarifa_hora === '' || isNaN(t) ? TARIFA_POR_DEFECTO : t;
  };
  const tarifaBase = tarifaDe(base);
  const grupos = [];
  const observaciones = [];
  const envios = [];
  const estados = new Set();
  partes.forEach(([j, o]) => {
    const tarifa = tarifaDe(o);
    let conPersonas = false;
    (o.grupos || []).forEach((g) => {
      if (!g || typeof g !== 'object') return;
      const nuevo = { ...g, jornada_id: j.id, fecha: fecha10(j.fecha) };
      if (Math.abs(tarifa - tarifaBase) > 1e-9 && (g.tarifa_hora === null || g.tarifa_hora === undefined || g.tarifa_hora === '')) {
        nuevo.tarifa_hora = tarifa;
      }
      grupos.push(nuevo);
      if ((parseInt(g.num_personas, 10) || 0) > 0) conPersonas = true;
    });
    const texto = String(o.observaciones || '').trim();
    if (texto && !observaciones.includes(texto)) observaciones.push(texto);
    (o.envios || []).forEach(e => { if (e && typeof e === 'object') envios.push(e); });
    if (conPersonas) estados.add(String(o.estado || 'borrador').toLowerCase());
  });
  envios.sort((a, b) => String(a.fecha || '').localeCompare(String(b.fecha || '')));
  const orden = {
    proveedor_id: base.proveedor_id ?? null,
    proveedor_nombre: base.proveedor_nombre ?? PROVEEDOR_POR_DEFECTO,
    proveedor_email: base.proveedor_email ?? '',
    proveedor_telefono: base.proveedor_telefono ?? '',
    tarifa_hora: tarifaBase,
    grupos,
    observaciones: observaciones.join('\n'),
    estado: estados.size === 1 && estados.has('enviada') ? 'enviada' : 'borrador',
    envios,
    fusionada_de_jornadas: true,
  };
  return { ...orden, ...calcularCosteOrdenPersonal(orden) };
};

// ---------------------------------------------------------------------------
// Helpers de UI
// ---------------------------------------------------------------------------

const nuevoIdGrupo = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `grupo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

/** Normaliza una hora a "HH:MM" (el presupuesto puede traerla en otros formatos). */
const normalizarHora = (hora) => {
  if (!hora) return '';
  if (/^\d{2}:\d{2}$/.test(hora)) return hora;
  const fecha = new Date(hora);
  if (!isNaN(fecha.getTime())) {
    return `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;
  }
  return '';
};

const sumarHoras = (horaHHMM, horas) => {
  if (!horaHHMM) return '';
  const [h, m] = horaHHMM.split(':').map((v) => parseInt(v, 10));
  if (isNaN(h) || isNaN(m)) return '';
  const total = (h * 60 + m + horas * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const formatEuro = (n) => (n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatHoras = (h) => (Number.isInteger(h) ? `${h}` : h.toFixed(1).replace('.', ','));

/** "de 08:00 a 10:30 y de 14:00 a 16:30" (con la fecha de cada tramo si la lleva) */
const textoTramos = (tramos) => {
  const partes = (tramos || []).map((t) => {
    const f = t.fecha ? `el ${String(t.fecha).slice(8, 10)}/${String(t.fecha).slice(5, 7)} ` : '';
    return `${f}de ${t.inicio} a ${t.fin}`;
  });
  if (partes.length <= 1) return partes[0] || '';
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
};

/** "dd/mm/aaaa" a partir de una fecha ISO (YYYY-MM-DD). Vacío si no es válida. */
const formatFechaCorta = (iso) => {
  if (!iso) return '';
  const partes = String(iso).slice(0, 10).split('-');
  if (partes.length !== 3) return String(iso);
  const [anio, mes, dia] = partes;
  return `${dia}/${mes}/${anio}`;
};

/**
 * Deja el teléfono en el formato que espera wa.me: solo dígitos y con prefijo
 * de país. Un móvil/fijo español (9 dígitos que empiezan por 6/7/8/9) se
 * prefija con 34; cualquier otro se asume ya internacional.
 */
const normalizarTelefono = (tel) => {
  const digitos = String(tel || '').replace(/\D/g, '');
  if (digitos.length === 9 && /^[6789]/.test(digitos)) return `34${digitos}`;
  return digitos;
};

/** "dd/mm/aaaa hh:mm" a partir de la fecha ISO que guarda el backend. */
const formatFechaHora = (iso) => {
  if (!iso) return '';
  const fecha = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (isNaN(fecha.getTime())) return '';
  const dd = String(fecha.getDate()).padStart(2, '0');
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const hh = String(fecha.getHours()).padStart(2, '0');
  const min = String(fecha.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${fecha.getFullYear()} ${hh}:${min}`;
};

/** Lee un campo del menú personalizado: nivel raíz y, si no, `config_personalizada`. */
const campoMenu = (menu, campo) => {
  if (menu?.[campo] !== undefined && menu?.[campo] !== null) return menu[campo];
  return menu?.config_personalizada?.[campo];
};

/**
 * Orden nueva (sin guardar) con uno o varios grupos ya preparados, su tarifa y,
 * opcionalmente, observaciones (p. ej. el detalle de "Quién va al evento").
 */
const construirOrdenNueva = (grupos, tarifaHora, observaciones = '') => {
  const nueva = {
    proveedor_id: null,
    proveedor_nombre: PROVEEDOR_POR_DEFECTO,
    proveedor_email: EMAIL_POR_DEFECTO,
    proveedor_telefono: TELEFONO_POR_DEFECTO,
    tarifa_hora: tarifaHora,
    grupos,
    total_personas: 0,
    total_horas: 0,
    coste_total: 0,
    estado: 'borrador',
    envios: [],
  };
  if (observaciones) nueva.observaciones = observaciones;
  return { ...nueva, ...calcularCosteOrdenPersonal(nueva) };
};

/** Grupos de la orden a partir de la sugerencia de "Quién va al evento" (uno por línea). */
const gruposDesdeSugerencia = (sugerencia, extra = {}) => (sugerencia?.grupos || []).map((g) => ({
  id: nuevoIdGrupo(),
  // Qué cubre el grupo en el coste del presupuesto (responsable o personal del menú)
  ...(g.origen ? { origen: g.origen } : {}),
  num_personas: Math.max(1, parseInt(g.num_personas, 10) || 1),
  hora_inicio: g.hora_inicio || '',
  hora_fin: g.hora_fin || '',
  // La misma persona en dos tramos (se va y vuelve a recoger): 1 persona
  ...(Array.isArray(g.tramos) && g.tramos.length > 0 ? { tramos: g.tramos.map(t => ({ ...t })) } : {}),
  horas: g.hora_inicio && g.hora_fin ? null : (g.horas ?? HORAS_POR_DEFECTO),
  // Servicio del grupo y su hora (solo informativo; viaja con la orden al guardar)
  ...(g.servicio ? {
    servicio: g.servicio,
    servicio_hora_inicio: g.servicio_hora_inicio || '',
    servicio_hora_fin: g.servicio_hora_fin || '',
    ...(g.sin_hora_servicio ? { sin_hora_servicio: true } : {}),
  } : {}),
  // Multievento: la jornada del grupo
  ...extra,
}));

/** Borrador de orden a partir de la sugerencia de "Quién va al evento" (un grupo por línea). */
const ordenDesdeSugerencia = (sugerencia) => construirOrdenNueva(
  gruposDesdeSugerencia(sugerencia),
  sugerencia.tarifa_hora ?? TARIFA_POR_DEFECTO,
  sugerencia.observaciones || '',
);

/** Multievento: borrador con la sugerencia de cada jornada (un bloque por jornada). */
const ordenDesdeSugerenciasJornadas = (jornadas, sugerencias) => {
  const grupos = [];
  const observaciones = [];
  let tarifa = null;
  (jornadas || []).forEach((j, i) => {
    const sug = sugerencias?.[j.id];
    if (!sug) return;
    grupos.push(...gruposDesdeSugerencia(sug, { jornada_id: j.id, fecha: fecha10(j.fecha) }));
    if (tarifa === null && sug.tarifa_hora !== undefined && sug.tarifa_hora !== null) tarifa = sug.tarifa_hora;
    if (sug.observaciones) observaciones.push(`Jornada ${i + 1}: ${sug.observaciones}`);
  });
  if (grupos.length === 0) return null;
  return construirOrdenNueva(grupos, tarifa ?? TARIFA_POR_DEFECTO, observaciones.join('\n'));
};

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

// sugerencia (opcional, formato nuevo): { grupos: [{ num_personas, hora_inicio,
// hora_fin, horas }], total_personas, tarifa_hora, observaciones }. Si no hay
// orden guardada, se abre un borrador con esos datos (sin guardar hasta pulsar
// "Guardar orden").
// sugerenciasJornada (multievento): { jornada_id: sugerencia } (la de cada jornada).
// `jornada`: modo antiguo (una orden por jornada); ya no se usa desde la ficha.
const OrdenPersonalExtra = ({ presupuesto, jornada = null, onUpdate, sugerencia = null, sugerenciasJornada = null }) => {
  const multi = !jornada && esMultievento(presupuesto);
  const jornadasP = multi ? (presupuesto?.jornadas || []) : [];
  const raizMulti = multi && presupuesto?.orden_personal_extra && typeof presupuesto.orden_personal_extra === 'object'
    ? presupuesto.orden_personal_extra : null;
  let ordenGuardada;
  if (jornada) ordenGuardada = jornada.orden_personal_extra || null;
  else if (multi) ordenGuardada = raizMulti && (raizMulti.grupos || []).length > 0 ? raizMulti : null;
  else ordenGuardada = presupuesto?.orden_personal_extra || null;
  // Multievento sin orden en la raíz: las órdenes antiguas por jornada, juntas
  const ordenFusionada = multi && !raizMulti ? fusionarOrdenesJornadas(presupuesto) : null;
  // Clave serializada: permite re-sincronizar el estado local sin meter un
  // objeto inestable en las dependencias del efecto.
  const ordenGuardadaKey = JSON.stringify(ordenGuardada || null);
  const ordenFusionadaKey = JSON.stringify(ordenFusionada || null);
  const sugerenciaKey = JSON.stringify(sugerencia || null);
  const sugerenciasJornadaKey = JSON.stringify(multi ? (sugerenciasJornada || null) : null);
  const jornadasKey = JSON.stringify(jornadasP.map(j => [j.id, j.fecha]));

  const [orden, setOrden] = useState(() => (ordenGuardada ? JSON.parse(JSON.stringify(ordenGuardada)) : null));
  const [editandoProveedor, setEditandoProveedor] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState(null); // { tipo: 'ok' | 'error', texto }

  // Re-sincroniza el estado local cuando cambia la orden guardada (recarga del
  // presupuesto). No se toca el mensaje: tras guardar, el padre re-renderiza y
  // borraría el aviso de éxito al instante.
  // Sin orden guardada y con sugerencia, se prepara el borrador con ella.
  // Multievento sin orden en la raíz y con órdenes antiguas por jornada: se
  // juntan en una (borrador para guardar).
  useEffect(() => {
    if (ordenGuardadaKey !== 'null') {
      setOrden(JSON.parse(ordenGuardadaKey));
    } else if (ordenFusionadaKey !== 'null') {
      setOrden(JSON.parse(ordenFusionadaKey));
    } else if (sugerenciasJornadaKey !== 'null') {
      setOrden(ordenDesdeSugerenciasJornadas(
        JSON.parse(jornadasKey).map(([id, fecha]) => ({ id, fecha })), JSON.parse(sugerenciasJornadaKey)));
    } else {
      setOrden(sugerenciaKey === 'null' ? null : ordenDesdeSugerencia(JSON.parse(sugerenciaKey)));
    }
  }, [ordenGuardadaKey, ordenFusionadaKey, sugerenciaKey, sugerenciasJornadaKey, jornadasKey]);

  // --- Menús aplicables y sugerido -----------------------------------------
  const menusDelRaiz = presupuesto?.menus_personalizados || [];
  const menusPropiosJornada = Array.isArray(jornada?.menus_personalizados) ? jornada.menus_personalizados : [];
  const usaMenusDelRaiz = jornada ? menusPropiosJornada.length === 0 : false;
  const menusAplicables = jornada ? (usaMenusDelRaiz ? menusDelRaiz : menusPropiosJornada) : menusDelRaiz;

  // Pax del menú. En una jornada que reutiliza los menús del raíz, el
  // `num_personas` del menú es el agregado de todas las jornadas, así que se usa
  // el de la jornada (modelo uniforme: mismos menús cada día).
  const paxDeMenu = (menu) => {
    if (jornada) {
      if (usaMenusDelRaiz) return jornada.num_personas || presupuesto?.num_personas || 0;
      return menu?.num_personas ?? jornada.num_personas ?? presupuesto?.num_personas ?? 0;
    }
    return menu?.num_personas ?? presupuesto?.num_personas ?? 0;
  };

  const sugeridosDe = (menus, pax) => menus.reduce((total, menu) => {
    const ratio = campoMenu(menu, 'ratio_camareros');
    if (!ratio || ratio <= 0) return total;
    const personas = pax(menu);
    if (!personas || personas <= 0) return total;
    return total + Math.ceil(personas / ratio);
  }, 0);
  const camarerosSugeridos = sugeridosDe(menusAplicables, paxDeMenu);
  // Multievento: sugerido de cada jornada con sus menús (o los del raíz con sus personas)
  const sugeridosJornada = (j) => {
    const propios = Array.isArray(j?.menus_personalizados) ? j.menus_personalizados : [];
    if (propios.length === 0) return sugeridosDe(menusDelRaiz, () => j?.num_personas || presupuesto?.num_personas || 0);
    return sugeridosDe(propios, m => m?.num_personas ?? j?.num_personas ?? presupuesto?.num_personas ?? 0);
  };

  const tarifaSugerida = (() => {
    for (const menu of (multi ? [...jornadasP.flatMap(j => j.menus_personalizados || []), ...menusDelRaiz] : menusAplicables)) {
      const tarifa = campoMenu(menu, 'coste_camarero_hora');
      if (tarifa && tarifa > 0) return tarifa;
    }
    return TARIFA_POR_DEFECTO;
  })();

  // --- Totales en vivo ------------------------------------------------------
  const totales = calcularCosteOrdenPersonal(orden);
  const hayCambios = JSON.stringify(orden || null) !== ordenGuardadaKey;
  // 'anulada': el evento se canceló con la orden enviada (se ve como tal)
  const estado = orden?.estado === 'enviada' ? 'enviada' : (orden?.estado === 'anulada' ? 'anulada' : 'borrador');
  const envios = Array.isArray(orden?.envios) ? orden.envios : [];
  const ultimoEnvio = envios.length > 0 ? envios[envios.length - 1] : null;
  // Descarga y envío trabajan sobre lo GUARDADO en BD: con cambios pendientes
  // el PDF/email saldrían desactualizados.
  const emailProveedor = (orden?.proveedor_email || '').trim();
  const telefonoProveedor = (orden?.proveedor_telefono || '').trim();
  const puedeDescargar = !!orden && !hayCambios && totales.total_personas > 0;
  const puedeEnviar = puedeDescargar && !!emailProveedor;
  const puedeWhatsapp = puedeDescargar && !!telefonoProveedor;

  // --- Mutaciones -----------------------------------------------------------
  const crearOrdenConSugerido = () => {
    if (multi) {
      // Con Quién va: la sugerencia de cada jornada; si no, los camareros de los menús
      const desdeQuienVa = ordenDesdeSugerenciasJornadas(jornadasP, sugerenciasJornada);
      if (desdeQuienVa) {
        setOrden(desdeQuienVa);
        setMensaje(null);
        return;
      }
      const grupos = jornadasP.map((j) => {
        const n = sugeridosJornada(j);
        const horaInicio = normalizarHora(j.hora_inicio || presupuesto?.hora_inicio);
        return n > 0 ? {
          id: nuevoIdGrupo(),
          jornada_id: j.id,
          fecha: fecha10(j.fecha),
          num_personas: n,
          hora_inicio: horaInicio,
          hora_fin: horaInicio ? sumarHoras(horaInicio, HORAS_POR_DEFECTO) : '',
          horas: horaInicio ? null : HORAS_POR_DEFECTO,
        } : null;
      }).filter(Boolean);
      if (grupos.length === 0 && jornadasP.length > 0) {
        const j = jornadasP[0];
        const horaInicio = normalizarHora(j.hora_inicio || presupuesto?.hora_inicio);
        grupos.push({
          id: nuevoIdGrupo(), jornada_id: j.id, fecha: fecha10(j.fecha), num_personas: 1,
          hora_inicio: horaInicio, hora_fin: horaInicio ? sumarHoras(horaInicio, HORAS_POR_DEFECTO) : '',
          horas: horaInicio ? null : HORAS_POR_DEFECTO,
        });
      }
      setOrden(construirOrdenNueva(grupos, tarifaSugerida));
      setMensaje(null);
      return;
    }
    if (sugerencia) {
      setOrden(ordenDesdeSugerencia(sugerencia));
      setMensaje(null);
      return;
    }
    const horaInicio = normalizarHora(jornada?.hora_inicio || presupuesto?.hora_inicio);
    const grupo = {
      id: nuevoIdGrupo(),
      num_personas: Math.max(1, camarerosSugeridos),
      hora_inicio: horaInicio,
      hora_fin: horaInicio ? sumarHoras(horaInicio, HORAS_POR_DEFECTO) : '',
      // Sin hora de evento no se puede derivar el horario: se fijan las horas.
      horas: horaInicio ? null : HORAS_POR_DEFECTO,
    };
    setOrden(construirOrdenNueva([grupo], tarifaSugerida));
    setMensaje(null);
  };

  const actualizarOrden = (campos) => {
    setOrden((prev) => (prev ? { ...prev, ...campos } : prev));
    setMensaje(null);
  };

  const actualizarGrupo = (grupoId, campos) => {
    setOrden((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        grupos: (prev.grupos || []).map((g) => {
          if (g.id !== grupoId) return g;
          const nuevo = { ...g, ...campos };
          // Con un solo tramo guardado (p. ej. recogida con fecha), las horas del grupo mandan
          if (Array.isArray(g.tramos) && g.tramos.length > 0 && ('hora_inicio' in campos || 'hora_fin' in campos)) {
            nuevo.tramos = g.tramos.map((t, i) => (i === 0 ? { ...t, inicio: nuevo.hora_inicio, fin: nuevo.hora_fin } : t));
          }
          return nuevo;
        }),
      };
    });
    setMensaje(null);
  };

  // Tramos de la misma persona: el primero manda en hora_inicio / hora_fin
  // (compatibilidad con lo que lee el resto del ERP)
  const actualizarTramo = (grupoId, indice, campos) => {
    setOrden((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        grupos: (prev.grupos || []).map((g) => {
          if (g.id !== grupoId) return g;
          const tramos = (g.tramos || []).map((t, i) => (i === indice ? { ...t, ...campos } : t));
          return { ...g, tramos, hora_inicio: tramos[0]?.inicio || '', hora_fin: tramos[0]?.fin || '', horas: null };
        }),
      };
    });
    setMensaje(null);
  };

  const anadirTramo = (grupoId) => {
    setOrden((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        grupos: (prev.grupos || []).map((g) => {
          if (g.id !== grupoId) return g;
          const base = (g.tramos && g.tramos.length > 0) ? g.tramos : [{ inicio: g.hora_inicio || '', fin: g.hora_fin || '' }];
          return { ...g, tramos: [...base, { inicio: '', fin: '' }], horas: null };
        }),
      };
    });
    setMensaje(null);
  };

  const quitarTramo = (grupoId, indice) => {
    setOrden((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        grupos: (prev.grupos || []).map((g) => {
          if (g.id !== grupoId) return g;
          const tramos = (g.tramos || []).filter((_, i) => i !== indice);
          const limpio = { ...g, hora_inicio: tramos[0]?.inicio || '', hora_fin: tramos[0]?.fin || '' };
          if (tramos.length > 1) return { ...limpio, tramos };
          delete limpio.tramos;
          return limpio;
        }),
      };
    });
    setMensaje(null);
  };

  // `jornadaDestino` (multievento): el grupo nuevo va a esa jornada, con las
  // horas del último grupo de la jornada o, si no tiene, las de la jornada
  const anadirGrupo = (jornadaDestino = null) => {
    setOrden((prev) => {
      if (!prev) return prev;
      const grupos = prev.grupos || [];
      if (jornadaDestino) {
        const deJornada = grupos.filter(g => jornadaDeGrupo(presupuesto, g) === jornadaDestino.id);
        const ultimoJ = deJornada[deJornada.length - 1];
        const horaInicio = ultimoJ ? (ultimoJ.hora_inicio || '') : normalizarHora(jornadaDestino.hora_inicio || presupuesto?.hora_inicio);
        let horaFin = ultimoJ ? (ultimoJ.hora_fin || '') : '';
        if (!ultimoJ && horaInicio) horaFin = sumarHoras(horaInicio, HORAS_POR_DEFECTO);
        let horas = ultimoJ ? (ultimoJ.horas ?? null) : null;
        if (!ultimoJ && !horaInicio) horas = HORAS_POR_DEFECTO;
        return {
          ...prev,
          grupos: [...grupos, {
            id: nuevoIdGrupo(),
            jornada_id: jornadaDestino.id,
            fecha: fecha10(jornadaDestino.fecha),
            num_personas: 1,
            hora_inicio: horaInicio,
            hora_fin: horaFin,
            horas,
          }],
        };
      }
      const ultimo = grupos[grupos.length - 1];
      return {
        ...prev,
        grupos: [...grupos, {
          id: nuevoIdGrupo(),
          num_personas: 1,
          hora_inicio: ultimo?.hora_inicio || '',
          hora_fin: ultimo?.hora_fin || '',
          horas: ultimo?.horas ?? null,
        }],
      };
    });
    setMensaje(null);
  };

  // Multievento: rellena una jornada sin grupos con lo de "Quién va"
  const rellenarJornada = (j) => {
    const sug = sugerenciasJornada?.[j.id];
    if (!sug) return;
    setOrden(prev => (prev ? {
      ...prev,
      grupos: [...(prev.grupos || []), ...gruposDesdeSugerencia(sug, { jornada_id: j.id, fecha: fecha10(j.fecha) })],
    } : prev));
    setMensaje(null);
  };

  const eliminarGrupo = (grupoId) => {
    setOrden((prev) => (prev ? { ...prev, grupos: (prev.grupos || []).filter((g) => g.id !== grupoId) } : prev));
    setMensaje(null);
  };

  // --- Guardado -------------------------------------------------------------
  // `_origen`: el servidor solo toma de este guardado la orden (de la raíz o de
  // cada jornada), nunca el resto de la copia de `jornadas` que llega por props.
  // El PUT genérico de presupuestos recalcula y persiste `desglose_costes`; el
  // endpoint específico de jornada no lo hace, por eso en multievento se manda
  // el array completo de jornadas por el PUT genérico.
  const guardar = async (ordenExplicita = undefined) => {
    const base = ordenExplicita !== undefined ? ordenExplicita : orden;
    let ordenAGuardar = base ? { ...base, ...calcularCosteOrdenPersonal(base) } : null;
    if (ordenAGuardar && ordenAGuardar.fusionada_de_jornadas) {
      ordenAGuardar = { ...ordenAGuardar };
      delete ordenAGuardar.fusionada_de_jornadas;
    }

    setGuardando(true);
    setMensaje(null);
    try {
      if (jornada) {
        const jornadas = (presupuesto?.jornadas || []).map((j) => (
          j.id === jornada.id ? { ...j, orden_personal_extra: ordenAGuardar } : j
        ));
        await presupuestosAPI.update(presupuesto.id, { jornadas, _origen: ORIGEN_GUARDADO });
        setOrden(ordenAGuardar);
        if (onUpdate) onUpdate({ jornadas });
      } else {
        await presupuestosAPI.update(presupuesto.id, { orden_personal_extra: ordenAGuardar, _origen: ORIGEN_GUARDADO });
        setOrden(ordenAGuardar);
        if (onUpdate) onUpdate({ orden_personal_extra: ordenAGuardar });
      }
      setMensaje({ tipo: 'ok', texto: ordenAGuardar ? 'Orden guardada correctamente' : 'Orden eliminada' });
      return true;
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: 'No se pudo guardar la orden: ' + (error.response?.data?.detail || error.message),
      });
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const eliminarOrden = () => {
    if (!window.confirm('¿Eliminar la orden de personal extra? Se volverá al coste teórico de camareros de los menús.')) return;
    // Multievento: la orden de la raíz se queda sin grupos (así las antiguas por
    // jornada siguen sin usarse)
    if (multi) {
      guardar({ ...(orden || {}), grupos: [], estado: 'borrador', total_personas: 0, total_horas: 0, coste_total: 0 });
      return;
    }
    guardar(null);
  };

  // --- PDF y envío al proveedor --------------------------------------------
  const descargarPdf = () => {
    window.open(presupuestosAPI.ordenPersonalPdfUrl(presupuesto.id, jornada?.id || null), '_blank');
  };

  const enviarSolicitud = async () => {
    const resumen = `¿Enviar la solicitud de ${totales.total_personas} camarero${totales.total_personas === 1 ? '' : 's'} (${formatHoras(totales.total_horas)} h) a ${emailProveedor}?`;
    if (!window.confirm(resumen)) return;

    setEnviando(true);
    setMensaje(null);
    try {
      const { data } = await presupuestosAPI.enviarOrdenPersonal(presupuesto.id, jornada?.id || null);
      const envio = {
        fecha: new Date().toISOString(),
        destinatario: emailProveedor,
      };
      const ordenEnviada = {
        ...orden,
        estado: 'enviada',
        envios: [...(Array.isArray(orden?.envios) ? orden.envios : []), envio],
      };
      setOrden(ordenEnviada);
      if (jornada) {
        const jornadas = (presupuesto?.jornadas || []).map((j) => (
          j.id === jornada.id ? { ...j, orden_personal_extra: ordenEnviada } : j
        ));
        if (onUpdate) onUpdate({ jornadas });
      } else if (onUpdate) {
        onUpdate({ orden_personal_extra: ordenEnviada });
      }
      setMensaje({ tipo: 'ok', texto: data?.mensaje || `Solicitud enviada a ${emailProveedor}` });
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: 'No se pudo enviar la solicitud: ' + (error.response?.data?.detail || error.message),
      });
    } finally {
      setEnviando(false);
    }
  };

  /** Línea de un grupo para WhatsApp. */
  const lineaGrupoWhatsapp = (grupo) => {
    const numPersonas = parseInt(grupo?.num_personas, 10);
    if (isNaN(numPersonas) || numPersonas <= 0) return null;
    const horas = horasDeGrupo(grupo);
    const sufijo = numPersonas === 1 ? '' : 's';
    const tramosG = tramosDeGrupo(grupo);
    if (tramosG.length > 1) {
      return `- ${numPersonas} camarero${sufijo} (${numPersonas === 1 ? 'la misma persona' : 'las mismas personas'} en ${tramosG.length} tramos): ${textoTramos(tramosG)} (${formatHoras(horas)} h)`;
    }
    if (grupo.hora_inicio && grupo.hora_fin) {
      return `- ${numPersonas} camarero${sufijo} de ${grupo.hora_inicio} a ${grupo.hora_fin} (${formatHoras(horas)} h)`;
    }
    return `- ${numPersonas} camarero${sufijo} (${formatHoras(horas)} h)`;
  };

  /** Multievento: cada jornada con su fecha, horario, dirección y camareros, y el total. */
  const construirMensajeWhatsappMulti = () => {
    const empresa = (presupuesto?.cliente_nombre || presupuesto?.lugar_evento || '').trim();
    const bloques = jornadasP.map((j, i) => ({
      j, i, grupos: (orden?.grupos || []).filter(g => jornadaDeGrupo(presupuesto, g) === j.id && (parseInt(g.num_personas, 10) || 0) > 0),
    })).filter(b => b.grupos.length > 0);
    const primera = formatFechaCorta(bloques[0]?.j.fecha);
    const ultima = formatFechaCorta(bloques[bloques.length - 1]?.j.fecha);
    const fechas = primera === ultima ? primera : `del ${primera} al ${ultima}`;
    const lineas = [
      'Hola,',
      '',
      `Necesitamos camareros para un evento de ${bloques.length} jornada${bloques.length === 1 ? '' : 's'} (${fechas})${empresa ? ` en ${empresa}` : ''}:`,
    ];
    bloques.forEach(({ j, i, grupos }) => {
      const tot = calcularCosteOrdenPersonal({ ...orden, grupos });
      const horario = j.hora_inicio && j.hora_fin ? `${j.hora_inicio} a ${j.hora_fin}` : (j.hora_inicio || 'horario por confirmar');
      const direccion = String(j.direccion_exacta || j.lugar_evento || presupuesto?.direccion_exacta || presupuesto?.lugar_evento || '').trim();
      lineas.push('');
      lineas.push(`Jornada ${i + 1}: ${formatFechaCorta(j.fecha)}, ${horario}.`);
      if (direccion) {
        lineas.push(direccion);
        lineas.push(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`);
      }
      grupos.forEach((g) => {
        const l = lineaGrupoWhatsapp(g);
        if (l) lineas.push(l);
      });
      lineas.push(`Total de la jornada: ${tot.total_personas} camarero${tot.total_personas === 1 ? '' : 's'}, ${formatHoras(tot.total_horas)} h.`);
    });
    lineas.push('');
    lineas.push(`Total del evento: ${totales.total_personas} camarero${totales.total_personas === 1 ? '' : 's'} sumando las jornadas, ${formatHoras(totales.total_horas)} h de servicio.`);
    const nombresPropios = (presupuesto?.personal_asignado || [])
      .map((x) => (x?.nombre || '').trim())
      .filter(Boolean);
    if (nombresPropios.length > 0) {
      const listado = nombresPropios.length === 1
        ? nombresPropios[0]
        : `${nombresPropios.slice(0, -1).join(', ')} y ${nombresPropios[nombresPropios.length - 1]}`;
      lineas.push(`Por nuestra parte ${nombresPropios.length === 1 ? 'va' : 'van'}: ${listado}.`);
    }
    if ((orden?.observaciones || '').trim()) {
      lineas.push(`Observaciones: ${orden.observaciones.trim()}`);
    }
    lineas.push('');
    lineas.push('Gracias.');
    return lineas.join('\n');
  };

  /** Texto de la solicitud para WhatsApp (saltos de línea reales). */
  const construirMensajeWhatsapp = () => {
    if (multi) return construirMensajeWhatsappMulti();
    const fecha = formatFechaCorta(jornada?.fecha || presupuesto?.fecha_evento);
    // Empresa (razón social) para encabezar; si no la hay, se cae al lugar.
    const empresa = (presupuesto?.cliente_nombre
      || jornada?.lugar_evento
      || presupuesto?.lugar_evento
      || '').trim();
    // Dirección a la que tiene que ir el personal (línea propia + enlace a Maps).
    const direccion = (presupuesto?.direccion_exacta
      || jornada?.lugar_evento
      || presupuesto?.lugar_evento
      || '').trim();

    const lineas = [
      'Hola,',
      '',
      `Necesitamos ${totales.total_personas} camarero${totales.total_personas === 1 ? '' : 's'} para el evento del ${fecha}${empresa ? ` en ${empresa}` : ''}:`,
    ];

    if (direccion) {
      lineas.push(direccion);
      // WhatsApp hace clicable la URL por su cuenta; no se envuelve en nada.
      lineas.push(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`);
    }

    lineas.push('');

    (orden?.grupos || []).forEach((grupo) => {
      const numPersonas = parseInt(grupo?.num_personas, 10);
      if (isNaN(numPersonas) || numPersonas <= 0) return;
      const horas = horasDeGrupo(grupo);
      const sufijo = numPersonas === 1 ? '' : 's';
      const tramosG = tramosDeGrupo(grupo);
      if (tramosG.length > 1) {
        lineas.push(`- ${numPersonas} camarero${sufijo} (${numPersonas === 1 ? 'la misma persona' : 'las mismas personas'} en ${tramosG.length} tramos): ${textoTramos(tramosG)} (${formatHoras(horas)} h)`);
      } else if (grupo.hora_inicio && grupo.hora_fin) {
        lineas.push(`- ${numPersonas} camarero${sufijo} de ${grupo.hora_inicio} a ${grupo.hora_fin} (${formatHoras(horas)} h)`);
      } else {
        lineas.push(`- ${numPersonas} camarero${sufijo} (${formatHoras(horas)} h)`);
      }
    });

    lineas.push('');
    lineas.push(`Total: ${formatHoras(totales.total_horas)} h de servicio.`);

    // Personal propio de Duetto que va al evento (para que el proveedor sepa
    // con quién se encuentra allí). Si no hay nadie asignado, no sale la línea.
    const nombresPropios = (presupuesto?.personal_asignado || [])
      .map((p) => (p?.nombre || '').trim())
      .filter(Boolean);
    if (nombresPropios.length > 0) {
      const listado = nombresPropios.length === 1
        ? nombresPropios[0]
        : `${nombresPropios.slice(0, -1).join(', ')} y ${nombresPropios[nombresPropios.length - 1]}`;
      lineas.push(`Por nuestra parte ${nombresPropios.length === 1 ? 'va' : 'van'}: ${listado}.`);
    }

    if ((orden?.observaciones || '').trim()) {
      lineas.push(`Observaciones: ${orden.observaciones.trim()}`);
    }
    lineas.push('');
    lineas.push('Gracias.');

    return lineas.join('\n');
  };

  const enviarPorWhatsapp = async () => {
    const resumen = `¿Abrir WhatsApp a ${telefonoProveedor} con la solicitud de ${totales.total_personas} camarero${totales.total_personas === 1 ? '' : 's'} (${formatHoras(totales.total_horas)} h)?`;
    if (!window.confirm(resumen)) return;

    // La ventana se abre en el mismo tick del click: si se hiciera después del
    // await del guardado, el navegador la bloquearía como popup.
    const texto = construirMensajeWhatsapp();
    const destino = normalizarTelefono(telefonoProveedor);
    window.open(`https://wa.me/${destino}?text=${encodeURIComponent(texto)}`, '_blank');

    // El envío real lo hace el usuario en WhatsApp; aquí solo se deja constancia.
    const ordenEnviada = {
      ...orden,
      estado: 'enviada',
      envios: [...(Array.isArray(orden?.envios) ? orden.envios : []), {
        fecha: new Date().toISOString(),
        destinatario: telefonoProveedor,
        canal: 'whatsapp',
      }],
    };
    const guardado = await guardar(ordenEnviada);
    if (guardado) {
      setMensaje({ tipo: 'ok', texto: 'WhatsApp abierto; recuerda pulsar enviar en la app' });
    }
  };

  // Un grupo de camareros (personas y horario) con sus botones
  const renderGrupo = (grupo, idx) => {
    const horas = horasDeGrupo(grupo);
    const textoServicio = textoServicioGrupo(grupo);
    return (
      <div key={grupo.id || `grupo-${idx}`} className="flex flex-wrap items-end gap-2 bg-gray-50 rounded-lg p-2">
        {textoServicio && (
          <p className="basis-full text-xs text-gray-600" data-testid="orden-extra-servicio">
            {textoServicio}
            {grupo.sin_hora_servicio && !grupo.servicio_hora_inicio && (
              <span className="text-amber-700">: ponla en Líneas y menús para ver a qué hora empieza</span>
            )}
          </p>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Personas</label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => actualizarGrupo(grupo.id, { num_personas: Math.max(0, (parseInt(grupo.num_personas, 10) || 0) - 1) })}
              className="p-1 border border-gray-300 rounded hover:bg-gray-100"
              title="Quitar una persona"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <input
              type="number"
              min="0"
              value={grupo.num_personas ?? ''}
              onChange={(e) => actualizarGrupo(grupo.id, { num_personas: e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0 })}
              className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
            <button
              type="button"
              onClick={() => actualizarGrupo(grupo.id, { num_personas: (parseInt(grupo.num_personas, 10) || 0) + 1 })}
              className="p-1 border border-gray-300 rounded hover:bg-gray-100"
              title="Añadir una persona"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {(grupo.tramos || []).length > 1 ? (
          <div className="flex flex-col gap-1" data-testid="orden-extra-tramos">
            <span className="text-xs font-medium text-gray-600">
              {(parseInt(grupo.num_personas, 10) || 0) === 1 ? 'La misma persona' : 'Las mismas personas'} en {grupo.tramos.length} tramos
            </span>
            {grupo.tramos.map((t, i) => (
              <div key={`${grupo.id}-tramo-${i}`} className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
                {t.fecha && <span>{formatFechaCorta(t.fecha)}</span>}
                <input
                  type="time"
                  value={t.inicio || ''}
                  onChange={(e) => actualizarTramo(grupo.id, i, { inicio: e.target.value })}
                  aria-label={`Entrada del tramo ${i + 1}`}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                />
                <span>a</span>
                <input
                  type="time"
                  value={t.fin || ''}
                  onChange={(e) => actualizarTramo(grupo.id, i, { fin: e.target.value })}
                  aria-label={`Salida del tramo ${i + 1}`}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                />
                <button type="button" onClick={() => quitarTramo(grupo.id, i)} className="text-red-500 hover:underline">
                  Quitar
                </button>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Entrada</label>
              <input
                type="time"
                value={grupo.hora_inicio || ''}
                onChange={(e) => actualizarGrupo(grupo.id, { hora_inicio: e.target.value, horas: null })}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Salida</label>
              <input
                type="time"
                value={grupo.hora_fin || ''}
                onChange={(e) => actualizarGrupo(grupo.id, { hora_fin: e.target.value, horas: null })}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
              />
            </div>
          </>
        )}

        <div className="pb-1.5 text-sm text-gray-700">
          {horas > 0 ? `${formatHoras(horas)} h` : <span className="text-amber-600">sin horario</span>}
        </div>

        <button
          type="button"
          onClick={() => anadirTramo(grupo.id)}
          className="mb-1 text-xs font-semibold text-duetto-primary hover:underline"
          title="La misma persona vuelve más tarde (por ejemplo, a recoger)"
        >
          Añadir tramo
        </button>

        <button
          type="button"
          onClick={() => eliminarGrupo(grupo.id)}
          className="ml-auto mb-1 p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
          title="Eliminar grupo"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  // --- Render ---------------------------------------------------------------
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-4 h-4 text-duetto-primary" />
          Personal extra · Orden de pedido
        </h4>
        {orden && (
          <div className="text-right">
            <Badge className={estado === 'enviada'
              ? 'border-transparent bg-green-100 text-green-700 hover:bg-green-100'
              : (estado === 'anulada'
                ? 'border-transparent bg-red-100 text-red-700 hover:bg-red-100'
                : 'border-transparent bg-amber-100 text-amber-700 hover:bg-amber-100')}>
              {estado === 'enviada' ? 'Enviada' : (estado === 'anulada' ? 'Anulada' : 'Borrador')}
            </Badge>
            {estado === 'enviada' && ultimoEnvio && (
              <p className="mt-1 text-xs text-gray-500">
                Enviada el {formatFechaHora(ultimoEnvio.fecha)}
                {ultimoEnvio.destinatario ? ` a ${ultimoEnvio.destinatario}` : ''}
                {ultimoEnvio.canal === 'whatsapp' ? ' (WhatsApp)' : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Sugerido según los menús (multievento: en cada jornada) */}
      {multi ? (
        <div className="text-xs text-gray-600 mb-3" data-testid="orden-extra-multievento">
          Una sola orden para las {jornadasP.length} jornadas: un bloque por jornada con sus camareros y horarios, y un único envío al proveedor.
        </div>
      ) : (
      <div className="text-xs text-gray-600 mb-3">
        Sugerido según menús: <span className="font-semibold text-gray-900">{camarerosSugeridos} camarero{camarerosSugeridos === 1 ? '' : 's'}</span>
        {camarerosSugeridos === 0 && (
          <span className="text-gray-400"> · los menús no definen ratio de camareros</span>
        )}
      </div>
      )}
      {multi && sugerenciasJornada && Object.keys(sugerenciasJornada).length > 0 && (
        <div className="text-xs text-gray-600 mb-3" data-testid="orden-extra-sugerencia">
          Según "Quién va al evento":{' '}
          <span className="font-semibold text-gray-900">
            personal externo en {Object.keys(sugerenciasJornada).length} {Object.keys(sugerenciasJornada).length === 1 ? 'jornada' : 'jornadas'}
          </span>
          {!ordenGuardada && !ordenFusionada && ' · borrador preparado, revisa y guarda la orden'}
        </div>
      )}
      {orden?.fusionada_de_jornadas && (
        <p className="text-xs text-amber-700 mb-3" data-testid="orden-extra-fusionada">
          Había una orden en cada jornada: ahora van juntas en una sola, con un bloque por jornada. Revísala y pulsa Guardar orden.
        </p>
      )}
      {sugerencia && (
        <div className="text-xs text-gray-600 mb-3" data-testid="orden-extra-sugerencia">
          Según "Quién va al evento":{' '}
          <span className="font-semibold text-gray-900">
            {sugerencia.total_personas} {sugerencia.total_personas === 1 ? 'persona externa' : 'personas externas'}
            {' en '}{(sugerencia.grupos || []).length} {(sugerencia.grupos || []).length === 1 ? 'grupo' : 'grupos'}
          </span>
          {!ordenGuardada && ' · borrador preparado, revisa y guarda la orden'}
        </div>
      )}

      {!orden && (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 mb-3">
            No hay orden de personal extra. Al crearla, su coste real sustituye al coste teórico de camareros de los menús.
          </p>
          <button
            type="button"
            onClick={crearOrdenConSugerido}
            className="px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-lg hover:opacity-90 inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Crear orden con el sugerido
          </button>
        </div>
      )}

      {orden && (
        <div className="space-y-3">
          {/* Proveedor y tarifa: fijos, con lápiz para editar */}
          {!editandoProveedor ? (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div className="text-sm text-gray-700 min-w-0">
                <span className="font-medium">{orden.proveedor_nombre || 'Proveedor sin nombre'}</span>
                <span className="text-gray-400"> · </span>
                <span className="text-gray-600">{orden.proveedor_email || 'sin email'}</span>
                <span className="text-gray-400"> · </span>
                {orden.proveedor_telefono
                  ? <span className="text-gray-600">{orden.proveedor_telefono}</span>
                  : <span className="text-gray-400">sin teléfono</span>}
                <span className="text-gray-400"> · </span>
                <span className="text-gray-600">{orden.tarifa_hora ?? TARIFA_POR_DEFECTO} €/h</span>
              </div>
              <button
                type="button"
                onClick={() => setEditandoProveedor(true)}
                title="Editar proveedor, email, teléfono o tarifa"
                className="p-1.5 text-gray-400 hover:text-duetto-primary hover:bg-gray-100 rounded shrink-0"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
                  <input
                    type="text"
                    value={orden.proveedor_nombre || ''}
                    onChange={(e) => actualizarOrden({ proveedor_nombre: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={orden.proveedor_email || ''}
                    onChange={(e) => actualizarOrden({ proveedor_email: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono (WhatsApp)</label>
                  <input
                    type="tel"
                    value={orden.proveedor_telefono || ''}
                    onChange={(e) => actualizarOrden({ proveedor_telefono: e.target.value })}
                    placeholder="600 000 000"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tarifa (€/h)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={orden.tarifa_hora ?? ''}
                    onChange={(e) => actualizarOrden({ tarifa_hora: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setEditandoProveedor(false)}
                  className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Hecho
                </button>
              </div>
            </div>
          )}

          {/* Observaciones para el proveedor (salen en el PDF y en el email) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Observaciones para el proveedor (planta, acceso, persona de contacto...)
            </label>
            <textarea
              rows={2}
              value={orden.observaciones || ''}
              onChange={(e) => actualizarOrden({ observaciones: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>

          {/* Grupos de citación (multievento: un bloque por jornada en la misma orden) */}
          {multi ? (
            <div className="space-y-3" data-testid="orden-extra-jornadas">
              {jornadasP.map((j, i) => {
                const gruposJ = (orden.grupos || []).filter(g => jornadaDeGrupo(presupuesto, g) === j.id);
                const totJ = calcularCosteOrdenPersonal({ ...orden, grupos: gruposJ });
                const sugeridosJ = sugeridosJornada(j);
                const lugarJ = String(j.lugar_evento || '').trim();
                return (
                  <div key={j.id} className="rounded-lg border border-gray-200 p-2.5 space-y-2" data-testid={`orden-extra-jornada-${j.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-bold text-gray-900">
                        Jornada {i + 1}{j.fecha ? ` · ${formatFechaCorta(j.fecha)}` : ''}
                      </span>
                      <span className="text-xs text-gray-600">
                        Evento de {j.hora_inicio || '?'} a {j.hora_fin || '?'}{lugarJ ? ` · ${lugarJ}` : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Sugerido según menús: {sugeridosJ} camarero{sugeridosJ === 1 ? '' : 's'}
                      {sugeridosJ > 0 && totJ.total_personas !== sugeridosJ ? ` · pedido: ${totJ.total_personas}` : ''}
                    </p>
                    {gruposJ.length === 0 && (
                      <p className="text-xs text-gray-500 italic">Sin camareros en esta jornada.</p>
                    )}
                    {gruposJ.map(g => renderGrupo(g, (orden.grupos || []).indexOf(g)))}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => anadirGrupo(j)}
                        className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                        data-testid={`orden-extra-anadir-${j.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Añadir grupo a la jornada {i + 1}
                      </button>
                      {gruposJ.length === 0 && sugerenciasJornada?.[j.id] && (
                        <button
                          type="button"
                          onClick={() => rellenarJornada(j)}
                          className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Rellenar con Quién va
                        </button>
                      )}
                      {totJ.total_personas > 0 && (
                        <span className="text-xs text-gray-600" data-testid={`orden-extra-total-${j.id}`}>
                          Jornada {i + 1}: {totJ.total_personas} persona{totJ.total_personas === 1 ? '' : 's'} · {formatHoras(totJ.total_horas)} h
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div className="space-y-2">
            {(orden.grupos || []).length === 0 && (
              <p className="text-xs text-gray-500 italic">Sin grupos. Añade al menos uno con personas y horario.</p>
            )}
            {(orden.grupos || []).map((grupo, idx) => renderGrupo(grupo, idx))}

            <button
              type="button"
              onClick={() => anadirGrupo()}
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Añadir grupo
            </button>
          </div>
          )}

          {/* Aviso si el pedido no coincide con el sugerido */}
          {!multi && camarerosSugeridos > 0 && totales.total_personas !== camarerosSugeridos && (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              Sugerido: {camarerosSugeridos} · Pedido: {totales.total_personas}
            </p>
          )}

          {/* Totales */}
          <div className="border-t border-gray-200 pt-2 flex flex-wrap justify-between items-center gap-2">
            <span className="text-sm text-gray-600">
              Total: <span className="font-semibold text-gray-900">{totales.total_personas} persona{totales.total_personas === 1 ? '' : 's'}</span>
              {' · '}
              <span className="font-semibold text-gray-900">{formatHoras(totales.total_horas)} h</span>
              {' · '}
              <span className="font-bold text-duetto-primary">{formatEuro(totales.coste_total)} €</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={eliminarOrden}
                disabled={guardando}
                className="px-2.5 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Eliminar orden
              </button>
              <button
                type="button"
                onClick={() => guardar()}
                disabled={guardando || !hayCambios}
                className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Save className="w-4 h-4" />
                {guardando ? 'Guardando...' : 'Guardar orden'}
              </button>
            </div>
          </div>

          {/* Documento para el proveedor */}
          <div className="border-t border-gray-200 pt-2 flex flex-wrap justify-end items-center gap-2">
            <button
              type="button"
              onClick={descargarPdf}
              disabled={!puedeDescargar || enviando}
              title={hayCambios ? 'Guarda la orden antes de descargar el PDF' : 'Descargar la solicitud en PDF'}
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              Descargar PDF
            </button>
            <button
              type="button"
              onClick={enviarPorWhatsapp}
              disabled={!puedeWhatsapp || enviando || guardando}
              title={
                hayCambios
                  ? 'Guarda la orden antes de enviar'
                  : (!telefonoProveedor ? 'Falta el teléfono del proveedor' : 'Abrir WhatsApp con la solicitud preparada')
              }
              className="px-3 py-1.5 text-sm border border-green-600 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <MessageCircle className="w-4 h-4" />
              Enviar por WhatsApp
            </button>
            <button
              type="button"
              onClick={enviarSolicitud}
              disabled={!puedeEnviar || enviando || guardando}
              title={
                hayCambios
                  ? 'Guarda la orden antes de enviar'
                  : (!emailProveedor ? 'Falta el email del proveedor' : 'Enviar la solicitud por email al proveedor')
              }
              className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Send className="w-4 h-4" />
              {enviando
                ? 'Enviando...'
                : (estado === 'enviada'
                  ? 'Reenviar solicitud'
                  : `Solicitar a ${orden.proveedor_nombre || 'proveedor'}`)}
            </button>
          </div>

          {hayCambios && !guardando && (
            <p className="text-xs text-amber-700">Hay cambios sin guardar.</p>
          )}
        </div>
      )}

      {mensaje && (
        <p className={`mt-2 text-xs ${mensaje.tipo === 'ok' ? 'text-green-700' : 'text-red-600'}`}>
          {mensaje.texto}
        </p>
      )}
    </div>
  );
};

export default OrdenPersonalExtra;
